import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import {
  AppBar,
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "./../../images/Logo.png";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  {
    label: "About Us",
    type: "menu",
    items: [
      {
        label: "Our Services",
        to: "/our-services",
        desc: "Explore our comprehensive solutions",
      },
      {
        label: "Insight",
        to: "/blog",
        desc: "Industry insights and updates",
      },
      {
        label: "Location",
        to: "/location",
        desc: "Find our office locations",
      },
    ],
  },
  {
    label: "Specialization",
    type: "menu",
    items: [
      {
        label: "Engineering",
        to: "/engineering",
        desc: "Engineering professionals and solutions",
      },
      {
        label: "Science",
        to: "/science",
        desc: "Scientific research and expertise",
      },
      {
        label: "Medical(Non-clincal)",
        to: "/medicine",
        desc: "Medical and healthcare professionals",
      },
      {
        label: "Technology",
        to: "/technology",
        desc: "Tech expertise and IT solutions",
      },
      {
        label: "Accounting and Finance",
        to: "/accounting-and-finance-staffing",
        desc: "Financial and accounting services",
      },
      {
        label: "Insurance and Executive",
        to: "/insurance-and-executive",
        desc: "Insurance and executive staffing",
      },
      {
        label: "Legal",
        to: "/legal",
        desc: "Legal professionals and counsel",
      },
    ],
  },
  {
    label: "Staffing Services",
    to: "/staffing-services",

  },
  {
    label: "Industries",
    type: "menu",
    items: [
      {
        label: "Banking",
        to: "/banking",
        desc: "Banking and financial services solutions",
      },
      {
        label: "Insurance",
        to: "/insurance",
        desc: "Insurance industry expertise",
      },
      {
        label: "Retail/eCommerce",
        to: "/retail-ecommerce",
        desc: "Retail and eCommerce solutions",
      },
      {
        label: "Healthcare",
        to: "/healthcare",
        desc: "Healthcare and medical services",
      },
      {
        label: "Pharmaceuticals & Life Sciences",
        to: "/pharmaceuticals-life-sciences",
        desc: "Pharmaceutical industry specialists",
      },
      {
        label: "Telecom",
        to: "/telecom",
        desc: "Telecommunications solutions",
      },
    ],
  },
  { label: "Careers", to: "/careers" },
  { label: "Contact Us", to: "/contact-us" },
];

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openMenuFor, setOpenMenuFor] = useState(null);

  const handleOpenMenu = (event, id) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuFor(id);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setOpenMenuFor(null);
  };

  const hoverTimer = React.useRef(null);
  const handleMenuMouseLeave = () => {
    hoverTimer.current = window.setTimeout(() => {
      handleCloseMenu();
    }, 200);
  };

  const handleMenuMouseEnter = () => {
    if (hoverTimer.current) {
      window.clearTimeout(hoverTimer.current);
      hoverTimer.current = null;
    }
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: "background.paper",
        color: "text.primary",
        borderBottom: "1px solid rgba(0,0,0,0.1)",
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ minHeight: 70, gap: 2 }}>
          {/* Logo */}
          <Box
            component="a"
            href="/"
            sx={{
              display: "flex",
              alignItems: "center",
              mr: { xs: 1, md: 2 },
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="AlphaForce Solutions"
              sx={{ height: { xs: 40, md: 60 }, width: "auto" }}
            />
          </Box>

          {/* Desktop Navigation */}
          <Stack
            direction="row"
            spacing={{ xs: 0.5, md: 1 }}
            sx={{
              display: { xs: "none", lg: "flex" },
              alignItems: "center",
              marginLeft: "auto"
            }}
          >
            {NAV_ITEMS.map((item) =>
              item.type === "menu" ? (
                <Button
                  key={item.label}
                  endIcon={<KeyboardArrowDownIcon fontSize="small" />}
                  onMouseEnter={(e) => handleOpenMenu(e, item.label)}
                  sx={{
                    color: "text.primary",
                    fontWeight: 600,
                    textTransform: "none",
                    fontSize: "15px",
                    px: 1.5,
                    py: 1.5,
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {item.label}
                </Button>
              ) : (
                <Button
                  key={item.label}
                  onClick={() => navigate(item.to)}
                  sx={{
                    color:
                      location.pathname === item.to
                        ? "primary.main"
                        : "text.primary",
                    fontWeight: location.pathname === item.to ? 700 : 600,
                    textTransform: "none",
                    fontSize: "15px",
                    px: 1.5,
                    py: 1.5,
                    "&:hover": {
                      color: "primary.main",
                      bgcolor: "transparent",
                    },
                  }}
                >
                  {item.label}
                </Button>
              )
            )}
          </Stack>





          {/* Mobile Menu Button */}
          <IconButton
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{
              display: { xs: "inline-flex", lg: "none" },
              ml: "auto",
            }}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>

        {/* Mobile Menu */}
        {mobileOpen && (
          <Stack
            spacing={0}
            sx={{
              display: { xs: "flex", lg: "none" },
              pb: 2,
              borderTop: "1px solid rgba(0,0,0,0.08)",
            }}
          >
            {NAV_ITEMS.map((item) =>
              item.type === "menu" ? (
                <Box key={item.label}>
                  <Typography
                    variant="body2"
                    sx={{
                      px: 2,
                      py: 1.5,
                      color: "text.secondary",
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      textTransform: "uppercase",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Stack>
                    {item.items.map((sub) => (
                      <Button
                        key={sub.label}
                        variant="text"
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          color: "text.primary",
                          px: 4,
                          py: 1,
                          fontSize: "15px",
                        }}
                        onClick={() => {
                          navigate(sub.to);
                          setMobileOpen(false);
                        }}
                      >
                        {sub.label}
                      </Button>
                    ))}
                  </Stack>
                </Box>
              ) : (
                <Button
                  key={item.label}
                  onClick={() => {
                    navigate(item.to);
                    setMobileOpen(false);
                  }}
                  variant="text"
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    color: "text.primary",
                    px: 2,
                    py: 1.5,
                    fontWeight: 600,
                    fontSize: "0.9rem",
                  }}
                >
                  {item.label}
                </Button>
              )
            )}
          </Stack>
        )}
      </Container>

      {/* Desktop Dropdown Menu with Hover Effect */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        onMouseLeave={handleMenuMouseLeave}
        PaperProps={{
          onMouseEnter: handleMenuMouseEnter,
          onMouseLeave: handleMenuMouseLeave,
          sx: {
            mt: 0,
            borderRadius: 0,
            overflow: "visible",
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            border: "none",
            animation: "slideDown 0.2s ease-out",
            "@keyframes slideDown": {
              from: {
                opacity: 0,
                transform: "translateY(-8px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
        MenuListProps={{ disablePadding: true }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box
          sx={{
            minWidth: 320,
            bgcolor: "background.paper",
            py: 1,
          }}
        >
          {(NAV_ITEMS.find((n) => n.label === openMenuFor)?.items || []).map(
            (sub, idx, arr) => (
              <ListItemButton
                key={sub.label}
                onClick={() => {
                  navigate(sub.to);
                  handleCloseMenu();
                }}
                sx={{
                  py: 1.5,
                  px: 2.5,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: "rgba(42, 82, 152, 0.04)",
                    borderLeft: "3px solid #2a5298",
                    pl: 2.2,
                  },
                }}
              >
                <Typography sx={{ fontWeight: 700, fontSize: "0.95rem" }}>
                  {sub.label}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.25, fontSize: "0.8rem" }}
                >
                  {sub.desc}
                </Typography>
              </ListItemButton>
            )
          )}
        </Box>
      </Menu>
    </AppBar>
  );
}
